/**
 * CommunicationCenterPage — Phase 4 of the DCCF Email System.
 *
 * One unified admin shell for every Chamber communication surface. It does
 * NOT rewrite the existing deeply-featured pages — those remain fully
 * functional at their current URLs. Instead it:
 *
 *  1. Provides a modern 8-tab navigation (Overview / Campaigns / Newsletter /
 *     Automations / Templates / Audience / Delivery / Settings).
 *  2. Ships a brand-new OVERVIEW dashboard pulling live stats from the
 *     Phase-2 email_service diagnostics endpoint + Phase-5 audience counts.
 *  3. Deep-links to the existing specialist pages (`/admin/newsletter`,
 *     `/admin/newsletter-center`, `/admin/email-communications`) so every
 *     feature keeps working while staff learn the new layout.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Megaphone, Mail, Workflow, FileText, Users, Activity, Settings,
  CheckCircle2, AlertTriangle, Ban, Eye, MousePointer, Clock, Loader2, ExternalLink,
  TrendingUp, ShieldAlert,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'overview',    label: 'Overview',    icon: LayoutDashboard },
  { key: 'campaigns',   label: 'Campaigns',   icon: Megaphone },
  { key: 'newsletter',  label: 'Newsletter',  icon: Mail },
  { key: 'automations', label: 'Automations', icon: Workflow },
  { key: 'templates',   label: 'Templates',   icon: FileText },
  { key: 'audience',    label: 'Audience',    icon: Users },
  { key: 'delivery',    label: 'Delivery',    icon: Activity },
  { key: 'settings',    label: 'Settings',    icon: Settings },
];

const STATUS_META = {
  sent:        { label: 'Sent',       color: 'bg-slate-50 text-slate-700',       icon: Clock },
  delivered:   { label: 'Delivered',  color: 'bg-emerald-50 text-emerald-700',   icon: CheckCircle2 },
  opened:      { label: 'Opened',     color: 'bg-sky-50 text-sky-700',           icon: Eye },
  clicked:     { label: 'Clicked',    color: 'bg-indigo-50 text-indigo-700',     icon: MousePointer },
  bounced:     { label: 'Bounced',    color: 'bg-rose-50 text-rose-700',         icon: AlertTriangle },
  complained:  { label: 'Complained', color: 'bg-amber-50 text-amber-800',       icon: ShieldAlert },
  failed:      { label: 'Failed',     color: 'bg-rose-50 text-rose-700',         icon: AlertTriangle },
  suppressed:  { label: 'Suppressed', color: 'bg-slate-200 text-slate-600',      icon: Ban },
  duplicate:   { label: 'Duplicate',  color: 'bg-slate-100 text-slate-500',      icon: Ban },
};

const Stat = ({ label, value, hint, tone = 'default' }) => {
  const toneClasses = {
    default: 'bg-white border-slate-200 text-[#1a1a1a]',
    good:    'bg-emerald-50 border-emerald-200 text-emerald-900',
    warn:    'bg-amber-50 border-amber-200 text-amber-900',
    bad:     'bg-rose-50 border-rose-200 text-rose-900',
  }[tone] || 'bg-white border-slate-200';
  return (
    <div className={`rounded-xl border p-4 ${toneClasses}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-xs uppercase tracking-wide opacity-60 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-xs opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
};

const CommunicationCenterPage = () => {
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') || 'overview';
  const setTab = (key) => setParams({ tab: key });

  const [diag, setDiag] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [digestPreview, setDigestPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, a, dp] = await Promise.all([
        axios.get(`${API}/admin/email/diagnostics`, { withCredentials: true })
          .catch(() => ({ data: null })),
        axios.get(`${API}/admin/comms/audiences`, { withCredentials: true })
          .catch(() => ({ data: { audiences: [] } })),
        axios.get(`${API}/admin/newsletters/digest-preview`, { withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      setDiag(d.data);
      setAudiences(a.data?.audiences || []);
      setDigestPreview(dp.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const counts = diag?.event_counts_7d || {};
  const totalSent = (counts.sent || 0) + (counts.delivered || 0) + (counts.opened || 0) + (counts.clicked || 0);
  const bouncedPct = totalSent ? Math.round(((counts.bounced || 0) / totalSent) * 100) : 0;
  const complainedPct = totalSent ? Math.round(((counts.complained || 0) / totalSent) * 100) : 0;

  const dashboardUrgentActions = useMemo(() => {
    const items = [];
    if (!diag?.has_resend_key) {
      items.push({
        severity: 'bad',
        title: 'Resend API key is missing',
        hint: 'Emails will be mocked only — set RESEND_API_KEY in backend/.env to enable live sending.',
      });
    }
    if (diag?.has_resend_key && !diag?.webhook_secret_configured) {
      items.push({
        severity: 'warn',
        title: 'Webhook secret not configured',
        hint: 'Configure RESEND_WEBHOOK_SECRET to enable delivery/open/click tracking. See /app/memory/EMAIL_WEBHOOK_SETUP.md.',
      });
    }
    if (diag?.sender_email === 'onboarding@resend.dev') {
      items.push({
        severity: 'warn',
        title: 'Using Resend default sender',
        hint: 'Verify dominicanchamberfl.org on Resend (SPF/DKIM/DMARC) and switch SENDER_EMAIL to no-reply@dominicanchamberfl.org for better deliverability.',
      });
    }
    if (bouncedPct > 5) {
      items.push({
        severity: 'bad',
        title: `Bounce rate ${bouncedPct}% in last 7 days`,
        hint: 'Review the suppression list and consider cleaning your list before the next big send.',
      });
    }
    if (complainedPct > 0.5) {
      items.push({
        severity: 'bad',
        title: `Spam complaints ${complainedPct}% in last 7 days`,
        hint: 'Audit recent campaigns — this threatens sender reputation.',
      });
    }
    if (digestPreview && digestPreview.would_send === false) {
      items.push({
        severity: 'warn',
        title: 'Next weekly digest has no content',
        hint: 'The failsafe will NOT auto-send. Add eligible content or opt items into the newsletter.',
      });
    }
    return items;
  }, [diag, bouncedPct, complainedPct, digestPreview]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="communication-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <Mail className="w-3.5 h-3.5" /> DCCF Platform
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mt-1">Communication Center</h1>
            <p className="text-sm text-slate-500 mt-1">
              One place for every Chamber email — campaigns, newsletters, automations, analytics.
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="text-sm font-semibold text-[#002249] hover:underline flex items-center gap-1.5"
            data-testid="refresh-overview"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-slate-200 p-1 flex overflow-x-auto gap-1 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === activeTab;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                  active ? 'bg-[#002249] text-white shadow' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <OverviewTab diag={diag} audiences={audiences} digestPreview={digestPreview}
                       loading={loading} counts={counts} totalSent={totalSent}
                       bouncedPct={bouncedPct} urgentActions={dashboardUrgentActions} />
        )}
        {activeTab === 'campaigns' && (
          <CampaignsTab />
        )}
        {activeTab === 'newsletter' && (
          <>
            <DigestPreviewCard digestPreview={digestPreview} />
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <LinkCard
                title="Newsletter Center (classic)"
                description="Visual block editor for the weekly digest, schedule, send and audience."
                cta="Open Newsletter Center"
                href="/admin/newsletter"
                testId="link-newsletter-main"
              />
              <LinkCard
                title="Newsletter Hub (alt)"
                description="Legacy admin view with events / communications tabs."
                cta="Open Hub"
                href="/admin/newsletter-center"
                testId="link-newsletter-hub"
              />
            </div>
          </>
        )}
        {activeTab === 'automations' && (
          <LinkCard
            title="Weekly Digest Automation"
            description="Failsafe settings — automatically send the digest if nothing has been published in N days. Phase 5 added a content-health guard so empty digests are never dispatched."
            cta="Configure Automations"
            href="/admin/newsletter?tab=automations"
            testId="link-automations"
          />
        )}
        {activeTab === 'templates' && (
          <LinkCard
            title="Email Templates"
            description="The classic Newsletter Center hosts the current template editor. A unified Template Center is on the Phase 6 roadmap."
            cta="Open Template Editor"
            href="/admin/newsletter?tab=templates"
            testId="link-templates"
          />
        )}
        {activeTab === 'audience' && (
          <AudienceTab audiences={audiences} />
        )}
        {activeTab === 'delivery' && (
          <DeliveryTab diag={diag} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab diag={diag} />
        )}
      </div>
    </div>
  );
};

// ─── Overview Tab ───────────────────────────────────────────────────────────

const OverviewTab = ({ diag, audiences, digestPreview, loading, counts, totalSent, bouncedPct, urgentActions }) => (
  <div className="space-y-5">
    {urgentActions.length > 0 && (
      <div className="space-y-2">
        {urgentActions.map((a, i) => (
          <div
            key={i}
            className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
              a.severity === 'bad'
                ? 'bg-rose-50 border-rose-200 text-rose-900'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
            data-testid={`urgent-${i}`}
          >
            {a.severity === 'bad'
              ? <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              : <TrendingUp className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-semibold">{a.title}</div>
              <div className="text-xs opacity-80 mt-0.5">{a.hint}</div>
            </div>
          </div>
        ))}
      </div>
    )}

    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <Stat label="Sent" value={counts.sent || 0} hint="last 7 days" />
      <Stat label="Delivered" value={counts.delivered || 0} tone="good" />
      <Stat label="Opened" value={counts.opened || 0} />
      <Stat label="Clicked" value={counts.clicked || 0} />
      <Stat
        label="Bounced"
        value={counts.bounced || 0}
        hint={`${bouncedPct}% of sent`}
        tone={bouncedPct > 5 ? 'bad' : 'default'}
      />
    </div>

    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Next Weekly Digest</div>
            <div className="font-bold text-[#1a1a1a] mt-0.5">
              {digestPreview?.would_send ? 'Ready to send' : 'Not enough content'}
            </div>
          </div>
          {digestPreview?.would_send
            ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            : <AlertTriangle className="w-5 h-5 text-amber-500" />}
        </div>
        {digestPreview?.sections?.length > 0 ? (
          <ul className="divide-y divide-slate-100 text-sm">
            {digestPreview.sections.map((s, i) => (
              <li key={i} className="py-2 flex justify-between items-center">
                <span className="capitalize text-slate-700">{s.label}</span>
                <span className={`tabular-nums font-semibold ${
                  s.item_count > 0 ? 'text-emerald-700' : 'text-slate-400'
                }`}>{s.item_count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-500">No preview available.</div>
        )}
        <Link
          to="/admin/newsletter"
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#002249] hover:underline mt-3"
        >
          Open Newsletter Center <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Audience Snapshot</div>
        {audiences.length === 0 && !loading && (
          <div className="text-sm text-slate-400">No audiences available.</div>
        )}
        <ul className="divide-y divide-slate-100 text-sm">
          {audiences.slice(0, 6).map((a) => (
            <li key={a.key} className="py-2 flex justify-between items-center">
              <span className="text-slate-700">{a.label}</span>
              <span className="tabular-nums font-semibold text-[#002249]">{a.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {diag && (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Recent Email Activity
        </div>
        {(diag.recent_events || []).length === 0 ? (
          <div className="text-sm text-slate-400">No events recorded yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {diag.recent_events.slice(0, 6).map((ev) => {
              const meta = STATUS_META[ev.last_status] || STATUS_META.sent;
              const Icon = meta.icon;
              return (
                <li key={ev.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-[#1a1a1a] truncate">
                      {ev.subject || '(no subject)'}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {ev.to} · {ev.category} · {new Date(ev.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${meta.color}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    )}
  </div>
);

// ─── Audience Tab ───────────────────────────────────────────────────────────

const AudienceTab = ({ audiences }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="p-5 border-b border-slate-100">
      <div className="font-bold text-[#1a1a1a]">Audience Segments</div>
      <div className="text-xs text-slate-500 mt-1">
        Live counts respect preferences and the suppression list. Phase-5 segments: board, committee, event attendees, staff.
      </div>
    </div>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="text-left px-5 py-2">Segment</th>
          <th className="text-right px-5 py-2">Members</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {audiences.map((a) => (
          <tr key={a.key} data-testid={`audience-row-${a.key}`}>
            <td className="px-5 py-3">
              <div className="font-semibold text-slate-700">{a.label}</div>
              <div className="text-xs text-slate-400 font-mono">{a.key}</div>
            </td>
            <td className="px-5 py-3 text-right tabular-nums font-semibold text-[#002249]">{a.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Delivery Tab ───────────────────────────────────────────────────────────

const DeliveryTab = ({ diag }) => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="font-bold text-[#1a1a1a] mb-3">7-day Delivery Breakdown</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(STATUS_META).map(([status, meta]) => (
          <div key={status} className={`rounded-lg p-3 border ${meta.color.replace('bg-','border-').replace('-50','-200').replace('-100','-200').replace('-200 text-','-200 ')}`}>
            <div className="text-xs font-semibold opacity-60 uppercase">{meta.label}</div>
            <div className="text-xl font-bold tabular-nums">{diag?.event_counts_7d?.[status] || 0}</div>
          </div>
        ))}
      </div>
    </div>
    {diag?.orphan_webhook_events > 0 && (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <div className="font-semibold">Orphan webhook events: {diag.orphan_webhook_events}</div>
        <div className="text-xs mt-1">
          These are delivery events for emails sent before Phase 2 (no matching email_events row).
          They're preserved in the <span className="font-mono">email_events_orphans</span> collection for audit.
        </div>
      </div>
    )}
  </div>
);

// ─── Settings Tab ───────────────────────────────────────────────────────────

const SettingsTab = ({ diag }) => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="font-bold text-[#1a1a1a] mb-4">Delivery Settings</div>
      <dl className="divide-y divide-slate-100 text-sm">
        <Row k="Resend API Key" v={diag?.has_resend_key ? 'Configured ✓' : 'Missing'} good={diag?.has_resend_key} />
        <Row k="Sender" v={`${diag?.sender_name || ''} <${diag?.sender_email || ''}>`} />
        <Row k="Reply-To" v={diag?.reply_to} />
        <Row k="Platform URL" v={diag?.platform_url} />
        <Row k="Webhook Secret" v={diag?.webhook_secret_configured ? 'Configured ✓' : 'Not configured'} good={diag?.webhook_secret_configured} />
      </dl>
    </div>
    <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
      Secrets live in <span className="font-mono">backend/.env</span>. See{' '}
      <span className="font-mono">/app/memory/EMAIL_WEBHOOK_SETUP.md</span> for the webhook setup walkthrough.
    </div>
  </div>
);

const Row = ({ k, v, good }) => (
  <div className="py-2 flex justify-between gap-4">
    <dt className="text-slate-500">{k}</dt>
    <dd className={`font-mono text-xs truncate ${good === true ? 'text-emerald-700' : good === false ? 'text-rose-600' : 'text-slate-700'}`}>
      {v || '—'}
    </dd>
  </div>
);

// ─── Generic deep-link card ─────────────────────────────────────────────────

const LinkCard = ({ title, description, cta, href, testId }) => (
  <Link
    to={href}
    className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-[#002249] hover:shadow-md transition-all"
    data-testid={testId}
  >
    <div className="font-bold text-[#1a1a1a] text-lg">{title}</div>
    <p className="text-sm text-slate-600 mt-1 mb-3 leading-relaxed">{description}</p>
    <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#002249]">
      {cta} <ExternalLink className="w-3.5 h-3.5" />
    </div>
  </Link>
);

// ─── Newsletter Tab helper ──────────────────────────────────────────────────

const DigestPreviewCard = ({ digestPreview }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Smart Weekly Digest</div>
        <div className="font-bold text-[#1a1a1a] mt-0.5">
          {digestPreview?.would_send ? 'Enough content — digest will send' : 'Not enough content yet'}
        </div>
      </div>
      {digestPreview?.would_send
        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        : <AlertTriangle className="w-5 h-5 text-amber-500" />}
    </div>
    {digestPreview?.sections?.length > 0 && (
      <div className="grid sm:grid-cols-3 gap-2">
        {digestPreview.sections.map((s, i) => (
          <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
            <div className="text-xs text-slate-500 capitalize">{s.label}</div>
            <div className={`text-lg font-bold tabular-nums ${s.item_count > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{s.item_count}</div>
            {s.sample_titles?.length > 0 && (
              <ul className="mt-1 text-xs text-slate-500 space-y-0.5">
                {s.sample_titles.map((t, j) => <li key={j} className="truncate">· {t}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── Campaigns Tab ─────────────────────────────────────────────────────────

const CAMPAIGN_STATUS = {
  draft:              { color: 'bg-slate-100 text-slate-700',    label: 'Draft' },
  scheduled:          { color: 'bg-indigo-50 text-indigo-700',   label: 'Scheduled' },
  sending:            { color: 'bg-amber-50 text-amber-800',     label: 'Sending…' },
  sent:               { color: 'bg-emerald-50 text-emerald-700', label: 'Sent' },
  sent_with_errors:   { color: 'bg-amber-50 text-amber-800',     label: 'Sent with errors' },
  failed:             { color: 'bg-rose-50 text-rose-700',       label: 'Failed' },
  paused:             { color: 'bg-slate-200 text-slate-600',    label: 'Cancelled' },
};

const CampaignsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API}/admin/campaigns?${params.toString()}`,
        { withCredentials: true });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, [search, statusFilter]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled campaign? It will not be sent.')) return;
    setCancelling(id);
    try {
      await axios.post(`${API}/admin/campaigns/${id}/cancel`, {}, { withCredentials: true });
      await load();
    } finally {
      setCancelling(null);
    }
  };

  const fmtET = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by subject or creator…"
          className="flex-1 min-w-[200px] px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          data-testid="campaigns-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          data-testid="campaigns-status-filter"
        >
          <option value="">All statuses</option>
          {Object.entries(CAMPAIGN_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <Link
          to="/admin/email-communications"
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#002249] text-white rounded-lg text-sm font-semibold hover:bg-[#001833]"
          data-testid="new-campaign"
        >
          + New Campaign
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading campaigns…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            No campaigns yet. Click <strong>+ New Campaign</strong> to compose your first one.
          </div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2 hidden sm:table-cell">Audience</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Updated</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c) => {
                const meta = CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft;
                return (
                  <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-[#1a1a1a]">{c.subject || '(no subject)'}</div>
                      <div className="text-xs text-slate-500">
                        {c.sent != null ? `${c.sent} sent · ` : ''}
                        {c.failed > 0 ? `${c.failed} failed · ` : ''}
                        {c.suppressed > 0 ? `${c.suppressed} suppressed · ` : ''}
                        by {c.created_by_name || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top hidden sm:table-cell text-slate-600">
                      {c.audience_label || c.audience || '—'}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
                        {meta.label}
                      </span>
                      {c.scheduled_at && c.status === 'scheduled' && (
                        <div className="text-xs text-slate-500 mt-1">⏰ {fmtET(c.scheduled_at)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top hidden md:table-cell text-slate-600">
                      {fmtET(c.updated_at)}
                    </td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                      <button
                        onClick={() => setSelected(c)}
                        className="text-xs font-semibold text-[#002249] hover:underline mr-2"
                        data-testid={`view-${c.id}`}
                      >
                        View
                      </button>
                      {c.status === 'scheduled' && (
                        <button
                          onClick={() => cancel(c.id)}
                          disabled={cancelling === c.id}
                          className="text-xs font-semibold text-rose-600 hover:underline"
                          data-testid={`cancel-${c.id}`}
                        >
                          {cancelling === c.id ? 'Cancelling…' : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <CampaignDetailModal campaign={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

const CampaignDetailModal = ({ campaign, onClose }) => {
  const [full, setFull] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/campaigns/${campaign.id}`, { withCredentials: true });
        setFull(data);
      } catch {
        setFull(campaign);
      }
    })();
  }, [campaign]);

  const stats = full?.event_stats || {};

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="campaign-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 overflow-hidden shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Campaign</div>
            <h2 className="text-xl font-bold text-[#1a1a1a] mt-0.5 truncate">{campaign.subject}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Sent" value={stats.sent || full?.sent || 0} />
            <Stat label="Delivered" value={stats.delivered || 0} tone="good" />
            <Stat label="Opened" value={stats.opened || 0} />
            <Stat label="Clicked" value={stats.clicked || 0} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="Bounced" value={stats.bounced || 0} tone={stats.bounced > 0 ? 'bad' : 'default'} />
            <Stat label="Complained" value={stats.complained || 0} tone={stats.complained > 0 ? 'bad' : 'default'} />
            <Stat label="Suppressed" value={full?.suppressed || 0} />
          </div>
          {full?.audit?.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Audit Log</div>
              <ul className="text-sm space-y-1.5">
                {full.audit.slice(-6).map((a, i) => (
                  <li key={i} className="text-slate-600">
                    <span className="font-mono text-xs text-slate-400">
                      {new Date(a.at).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'short' })}
                    </span>{' '}
                    <strong className="text-slate-700">{a.action}</strong>
                    {a.by && <> by {a.by}</>}
                    {a.sent != null && <> — {a.sent} sent</>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {full?.body_html && (
            <details className="rounded-lg border border-slate-200">
              <summary className="px-4 py-2 bg-slate-50 cursor-pointer font-semibold text-sm text-slate-700">
                HTML Preview
              </summary>
              <div className="p-4 border-t border-slate-200 max-h-80 overflow-auto"
                   dangerouslySetInnerHTML={{ __html: full.body_html }} />
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunicationCenterPage;